import { Controller } from "@hotwired/stimulus"

/*
 * Checkout payment form.
 *
 * Card entry belongs to Accept UI, Authorize.Net's hosted lightbox: the AcceptUI.js
 * library (loaded from their host by store/checkout/_pay_button) binds itself to the
 * `.AcceptUI` button, opens the lightbox on click, and hands the result to the global
 * function named in the button's data-responseHandler. This controller owns that global,
 * copies the returned nonce into the form's hidden fields, submits, and covers the two
 * ways that can fail: the library never loaded, or tokenization returned an error.
 *
 * No card data ever exists in this DOM; keep it that way (docs/checkout-card-tokenization.md).
 */
const RESPONSE_HANDLER = "csepubAcceptUiResponse"

const SCRIPT_BLOCKED_MESSAGE =
  "We couldn't load the secure card window from authorize.net. Check that nothing in your " +
  "browser is blocking it, then reload this page and try again. Nothing has been charged."

const TOKENIZATION_FAILED_MESSAGE =
  "Your card details couldn't be sent to our payment processor. Nothing has been charged. " +
  "Please try again."

export default class extends Controller {
  static targets = [
    "dataDescriptor",
    "dataValue",
    "submitButton",
    "submitText",
    "submitSpinner",
    "error",
  ]

  connect() {
    this.isSubmitting = false
    window[RESPONSE_HANDLER] = (response) => this.handleAcceptResponse(response)
  }

  disconnect() {
    if (window[RESPONSE_HANDLER]) delete window[RESPONSE_HANDLER]
  }

  // ── Pay button ────────────────────────────────────────────────────
  //
  // AcceptUI.js has its own click listener on this button and opens the lightbox; this
  // one only notices when that library isn't there (blocked by an extension, offline).

  onPayClick(event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }
    if (!window.AcceptUI) {
      event.preventDefault()
      this._showError(SCRIPT_BLOCKED_MESSAGE)
    }
  }

  // ── Accept UI response ────────────────────────────────────────────

  handleAcceptResponse(response) {
    const messages = (response && response.messages) || {}
    if (messages.resultCode === "Error") {
      const texts = (messages.message || []).map((m) => m.text).filter(Boolean)
      this._showError(texts.length ? texts.join(" ") : TOKENIZATION_FAILED_MESSAGE)
      return
    }

    const opaque = response && response.opaqueData
    if (!opaque || !opaque.dataValue) {
      this._showError(TOKENIZATION_FAILED_MESSAGE)
      return
    }

    this.dataDescriptorTarget.value = opaque.dataDescriptor
    this.dataValueTarget.value = opaque.dataValue
    this._clearError()
    this.element.requestSubmit()
  }

  // ── Form submission ───────────────────────────────────────────────

  handleSubmit(event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    // Enter in the ZIP field submits the form without a nonce; send it through the
    // lightbox instead of letting the server reject it.
    if (!this.dataValueTarget.value) {
      event.preventDefault()
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.click()
      return
    }

    this.isSubmitting = true
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("btn-pay--processing")
    }
    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.style.display = "none"
    }
    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.style.display = ""
    }
  }

  // ── Errors ────────────────────────────────────────────────────────

  _showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  _clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }
}
